import type { Patient, Reference } from '@medplum/fhirtypes'
import { Diagnosis } from '@seen/core/models/conditions/Diagnosis'
import {
  normalizeConditionTemplateTitle,
  OrganizationConditionsList,
} from '@seen/core/models/organizationConditionsList'
import { ExtendedOrganization } from '@seen/core/models/organizations/ExtendedOrganization'
import { useMemo } from 'react'
import type { ConditionPickerCondition } from '#src/components/condition-picker/types.ts'
import { useOrderedConditionsList } from '#src/hooks/useOrderedConditions.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'

interface UseConditionPickerDataOptions {
  patientRef: Reference<Patient> | undefined
}

interface UseConditionPickerDataResult {
  conditions: ConditionPickerCondition[] | undefined
  isLoading: boolean
  orgConditionsList: OrganizationConditionsList | undefined
  existingParticipantIcd10Codes: Set<string>
  existingOrganizationConditionTitles: Set<string>
}

export function useConditionPickerData({
  patientRef,
}: UseConditionPickerDataOptions): UseConditionPickerDataResult {
  const {
    conditions: rawConditions,
    orderedConditions,
    isLoading: isLoadingConditions,
  } = useOrderedConditionsList(patientRef)

  /* 2. Fetch organization conditions list */
  const { data: seenHealthOrg } = useSearchOne(
    ExtendedOrganization,
    ExtendedOrganization.querySeenHealthOrg()
  )

  const { data: orgConditionsList, isLoading: isLoadingOrg } = useSearchOne(
    OrganizationConditionsList,
    seenHealthOrg
      ? OrganizationConditionsList.byOrganization(seenHealthOrg.reference)
      : undefined
  )

  /* 3. Build combined, segmented list */
  const {
    conditions,
    existingParticipantIcd10Codes,
    existingOrganizationConditionTitles,
  } = useMemo(():
    | {
        conditions: ConditionPickerCondition[]
        existingParticipantIcd10Codes: Set<string>
        existingOrganizationConditionTitles: Set<string>
      }
    | {
        conditions: undefined
        existingParticipantIcd10Codes: Set<string>
        existingOrganizationConditionTitles: Set<string>
      } => {
    const emptySet = new Set<string>()
    if (!rawConditions) {
      return {
        conditions: undefined,
        existingParticipantIcd10Codes: emptySet,
        existingOrganizationConditionTitles: emptySet,
      }
    }
    const sortedRawConditions = Diagnosis.sortForDisplay(rawConditions)

    const result: ConditionPickerCondition[] = []

    /* Participant condition titles/codes for dedup */
    const participantConditionTitles = new Set<string>()
    const participantIcd10Codes = new Set<string>()
    for (const c of sortedRawConditions) {
      const normalizedTitle = normalizeConditionTemplateTitle(c.title)
      if (normalizedTitle) {
        participantConditionTitles.add(normalizedTitle)
      }
      const icd10Code = c.icd10Code?.code
      if (icd10Code) {
        participantIcd10Codes.add(icd10Code)
      }
    }

    /* Section 1: Active/controlled conditions (ordered) */
    const orderedSet = new Set(orderedConditions.map((c) => c.id))
    for (const c of orderedConditions) {
      result.push({
        id: c.id,
        title: c.title,
        icd10Code: c.icd10Code?.code,
        onsetDate: c.onsetDate,
        clinicalStatus: c.clinicalStatus,
        section: 'active',
        diagnosis: c,
      })
    }

    /* Section 2: Inactive conditions */
    for (const c of sortedRawConditions) {
      if (orderedSet.has(c.id)) {
        continue
      }
      result.push({
        id: c.id,
        title: c.title,
        icd10Code: c.icd10Code?.code,
        onsetDate: c.onsetDate,
        clinicalStatus: c.clinicalStatus,
        section: 'inactive',
        diagnosis: c,
      })
    }

    /* Section 3: Org-only conditions (deduped by normalized title) */
    const orgConditionTitles = new Set<string>()
    if (orgConditionsList) {
      for (const [
        templateIndex,
        template,
      ] of orgConditionsList.conditions.entries()) {
        const normalizedTemplateTitle = normalizeConditionTemplateTitle(
          template.title
        )
        if (normalizedTemplateTitle) {
          orgConditionTitles.add(normalizedTemplateTitle)
        }

        const templateIcd10Codes = template.icd10Codes
          .map((coding) => coding.code)
          .filter((code): code is string => !!code)

        if (
          normalizedTemplateTitle &&
          participantConditionTitles.has(normalizedTemplateTitle)
        ) {
          continue
        }

        const templateIdBase =
          normalizedTemplateTitle || `template-${templateIndex}`
        result.push({
          id: `org-template-${templateIdBase}-${templateIndex}`,
          title: template.title,
          icd10Code:
            templateIcd10Codes.length > 0
              ? templateIcd10Codes.join(', ')
              : undefined,
          section: 'organization',
          orgTemplate: template,
        })
      }
    }

    return {
      conditions: result,
      existingParticipantIcd10Codes: participantIcd10Codes,
      existingOrganizationConditionTitles: orgConditionTitles,
    }
  }, [rawConditions, orderedConditions, orgConditionsList])

  return {
    conditions,
    isLoading: isLoadingConditions || isLoadingOrg,
    orgConditionsList: orgConditionsList ?? undefined,
    existingParticipantIcd10Codes,
    existingOrganizationConditionTitles,
  }
}
